import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as admissionsService from "../service/admissionsService";
import type {
  AdmissionsState,
  CreateInquiryPayload,
  ConvertInquiryPayload,
  DirectAdmissionPayload,
  TransferAdmissionPayload,
  ReadmissionPayload,
  InquiryStatus,
  InquiryChannel,
} from "../types";
import i18n from "@/i18n";

export const fetchInquiries = createAsyncThunk(
  "admissions/fetchInquiries",
  async ({ institutionId, status, channel, page, limit }: { institutionId: number; status?: InquiryStatus; channel?: InquiryChannel; page?: number; limit?: number }) => {
    return await admissionsService.fetchInquiries(institutionId, { status, channel, page, limit });
  }
);

export const createInquiry = createAsyncThunk(
  "admissions/createInquiry",
  async ({ institutionId, payload }: { institutionId: number; payload: CreateInquiryPayload }, { dispatch }) => {
    await admissionsService.createInquiry(institutionId, payload);
    await dispatch(fetchInquiries({ institutionId }));
  }
);

export const updateInquiryStatus = createAsyncThunk(
  "admissions/updateInquiryStatus",
  async (
    { institutionId, inquiryId, status }: { institutionId: number; inquiryId: number; status: InquiryStatus },
    { dispatch }
  ) => {
    await admissionsService.updateInquiryStatus(institutionId, inquiryId, status);
    await dispatch(fetchInquiries({ institutionId }));
  }
);

export const convertInquiry = createAsyncThunk(
  "admissions/convertInquiry",
  async (
    { institutionId, inquiryId, payload }: { institutionId: number; inquiryId: number; payload: ConvertInquiryPayload },
    { dispatch }
  ) => {
    const result = await admissionsService.convertInquiry(institutionId, inquiryId, payload);
    await dispatch(fetchInquiries({ institutionId }));
    return result;
  }
);

export const admitDirect = createAsyncThunk(
  "admissions/admitDirect",
  async ({ institutionId, payload }: { institutionId: number; payload: DirectAdmissionPayload }) => {
    return await admissionsService.admitDirect(institutionId, payload);
  }
);

export const admitTransfer = createAsyncThunk(
  "admissions/admitTransfer",
  async ({ institutionId, payload }: { institutionId: number; payload: TransferAdmissionPayload }) => {
    return await admissionsService.admitTransfer(institutionId, payload);
  }
);

export const readmitStudent = createAsyncThunk(
  "admissions/readmitStudent",
  async ({ institutionId, studentId, payload }: { institutionId: number; studentId: number; payload: ReadmissionPayload }) => {
    return await admissionsService.readmitStudent(institutionId, studentId, payload);
  }
);

const initialState: AdmissionsState = {
  inquiries: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 1 },
  loading: false,
  submitting: false,
  error: null,
  lastResult: null,
};

const admissionsSlice = createSlice({
  name: "admissions",
  initialState,
  reducers: {
    clearLastResult(state) {
      state.lastResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInquiries.fulfilled, (state, action) => {
        state.loading = false;
        state.inquiries = action.payload.inquiries;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchInquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionsSlicetsMsgs.failedToLoadInquiries");
      })
      .addCase(createInquiry.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("admissions:admissionsSlicetsMsgs.failedToCreateInquiry");
      })
      .addCase(updateInquiryStatus.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("admissions:admissionsSlicetsMsgs.failedToUpdateInquiryStatus");
      })
      .addCase(convertInquiry.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(convertInquiry.fulfilled, (state, action) => {
        state.submitting = false;
        state.lastResult = action.payload;
      })
      .addCase(convertInquiry.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionsSlicetsMsgs.failedToConvertInquiry");
      })
      .addCase(admitDirect.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(admitDirect.fulfilled, (state, action) => {
        state.submitting = false;
        state.lastResult = action.payload;
      })
      .addCase(admitDirect.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionsSlicetsMsgs.failedToAdmitStudent");
      })
      .addCase(admitTransfer.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(admitTransfer.fulfilled, (state, action) => {
        state.submitting = false;
        state.lastResult = action.payload;
      })
      .addCase(admitTransfer.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionsSlicetsMsgs.failedToAdmitStudent");
      })
      .addCase(readmitStudent.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(readmitStudent.fulfilled, (state, action) => {
        state.submitting = false;
        state.lastResult = action.payload;
      })
      .addCase(readmitStudent.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.error.message ?? i18n.t("admissions:admissionsSlicetsMsgs.failedToReAdmitStudent");
      });
  },
});

export const { clearLastResult } = admissionsSlice.actions;
export default admissionsSlice.reducer;
